// rm-intro.js — post-Start "Speech Genie" intro video gateway.
//
// Sits on the route between Home's Start button and the root menu:
//
//   rm-home (Start) → /intro (this screen) → /list/root
//
// The Start click is a user gesture, so the video can play WITH SOUND and
// no extra tap — and that same gesture unlocks iOS Safari's audio for the
// rest of the session (see CLAUDE.md "Audio race on iOS"). For that to
// hold, `video.play()` must be called synchronously inside onConnected()
// before any `await`, so it runs within the click's activation window.
//
// Behaviour:
//   • Plays once per session (sessionStorage `rm-intro-seen`). Home checks
//     the flag and skips straight to /list/root on subsequent Starts.
//   • A persistent "Skip to menu" button ends the intro early and routes on.
//   • On `ended`, routes on automatically.
//   • If the video asset is missing / errors (it isn't produced yet), a
//     branded placeholder with a "Continue →" button stands in, so the
//     whole flow is testable before the real clip is published.
//
// The actual genie clip is expected at CDN `Media/RM123INTRO.mp4`
// (+ optional `.webm`). Script: docs/genie-intro-script.md.

import { RMComponent } from "../core/component.js";
import { navigate } from "../router.js";
import { resolveVideoSources } from "../data/media-paths.js";

/** sessionStorage flag — set once the intro has played or been skipped. */
export const INTRO_SEEN_KEY = "rm-intro-seen";

/** Genie welcome video filename (published to CDN `Media/`). Web-optimised
 *  720×1280 / faststart, ~3 MB — a distinct filename from the 39 MB master so
 *  the immutable CDN cache serves the new file without a purge. */
const INTRO_MEDIA = "RM123INTRO-720.mp4";

/** Where the gateway hands off to. */
const NEXT_ROUTE = "/list/root";

export class RMIntro extends RMComponent {
  static styles = `
    /* Fullscreen takeover sized to the viewport. position:fixed is a
       deliberate product choice: the genie intro covers the whole screen
       (no header/footer chrome) for a cinematic welcome, and sizing
       against the viewport keeps the video full-bleed regardless of the
       shell layout. (Historically this also dodged a bug where the
       app-shell's <main> resolved to 0px height — fixed in briefs/012 —
       but full-bleed is the intended look, so we keep position:fixed.)
       The Skip button is the exit; on _finish the element unmounts and
       the overlay goes with it. See briefs/010 and briefs/012. */
    :host { position: fixed; inset: 0; z-index: 50; background: #000; overflow: hidden; }
    .stage { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; }
    video { width: 100%; height: 100%; object-fit: contain; background: #000; }
    .skip {
      position: absolute; top: 0.75rem; right: 0.75rem; z-index: 2;
      appearance: none; cursor: pointer; font: inherit; font-size: 0.85rem; line-height: 1;
      color: #fff; background: rgba(0,0,0,0.45);
      border: 1px solid rgba(255,255,255,0.5); border-radius: 999px;
      padding: 0.5rem 0.9rem; backdrop-filter: blur(2px);
    }
    .skip:hover { background: rgba(0,0,0,0.7); }
    .tap {
      position: absolute; inset: 0; z-index: 1; margin: auto; width: max-content; height: max-content;
      appearance: none; cursor: pointer; font: inherit; color: #fff;
      background: rgba(0,0,0,0.5); border: 1px solid rgba(255,255,255,0.6);
      border-radius: 999px; padding: 0.8rem 1.4rem;
    }
    /* Branded placeholder shown when the real clip isn't available yet. */
    .ph {
      position: absolute; inset: 0; display: flex; flex-direction: column;
      align-items: center; justify-content: center; gap: 1.25rem; text-align: center;
      padding: 2rem; box-sizing: border-box; background: #f7941e; color: #fff;
    }
    .ph h2 { margin: 0; font-size: 1.6rem; font-weight: 500; }
    .ph p { margin: 0; max-width: 30rem; line-height: 1.5; opacity: 0.95; }
    .ph .mark { font-size: 0.7rem; letter-spacing: 0.28em; opacity: 0.85; }
    .ph .go {
      appearance: none; cursor: pointer; font: inherit; font-size: 1rem;
      color: #f7941e; background: #fff; border: none; border-radius: 0.5rem;
      padding: 0.7rem 1.5rem;
    }
    .ph .go:hover { filter: brightness(0.95); }
    [hidden] { display: none !important; }
  `;

  /** @type {boolean} */ _done = false;
  /** @type {HTMLVideoElement | null} */ _video = null;

  onConnected() {
    const root = this.shadowRoot;
    if (!root) return;

    const stage = document.createElement("div");
    stage.className = "stage";

    const video = document.createElement("video");
    video.id = "vid";
    video.playsInline = true;
    // @ts-ignore — Safari-specific attribute, harmless elsewhere.
    video.setAttribute("webkit-playsinline", "true");
    video.preload = "auto";
    for (const s of resolveVideoSources(INTRO_MEDIA)) {
      const src = document.createElement("source");
      src.src = s.url;
      src.type = s.type;
      video.appendChild(src);
    }
    this._video = video;
    stage.appendChild(video);

    const skip = document.createElement("button");
    skip.type = "button";
    skip.className = "skip";
    skip.textContent = "Skip ↪ menu";
    skip.addEventListener("click", () => this._finish());

    // "Tap to play" affordance — only revealed if autoplay-with-sound is
    // rejected (gesture window lost). Hidden by default.
    const tap = document.createElement("button");
    tap.type = "button";
    tap.className = "tap";
    tap.textContent = "▶ Play intro";
    tap.hidden = true;
    tap.addEventListener("click", () => {
      tap.hidden = true;
      video.play().catch(() => this._showPlaceholder());
    });

    root.replaceChildren(this._styleEl(), stage, skip, tap);

    // End / error wiring.
    video.addEventListener("ended", () => this._finish());
    video.addEventListener("error", () => this._showPlaceholder());
    // If no source can be loaded the browser fires `error` on the last
    // <source>; also guard with a stalled-load timeout for slow/missing CDN.
    const loadGuard = window.setTimeout(() => {
      if (video.readyState === 0) this._showPlaceholder();
    }, 12000);
    video.addEventListener("loadeddata", () => clearTimeout(loadGuard));
    this.addCleanup(() => clearTimeout(loadGuard));

    this.addCleanup(() => { try { video.pause(); } catch {} });

    // Fire play() NOW — synchronously, within the Start-click activation —
    // so iOS Safari allows sound. If it rejects, offer a tap affordance.
    const p = video.play();
    if (p && typeof p.catch === "function") {
      p.catch(() => { tap.hidden = false; });
    }
  }

  /** Build the <style> element from static styles (imperative render path). */
  _styleEl() {
    const style = document.createElement("style");
    style.textContent = RMIntro.styles || "";
    return style;
  }

  /** Swap in the branded placeholder (asset missing / not produced yet). */
  _showPlaceholder() {
    if (this._done) return;
    const root = this.shadowRoot;
    if (!root) return;
    try { this._video?.pause(); } catch {}

    const ph = document.createElement("div");
    ph.className = "ph";

    const h = document.createElement("h2");
    h.textContent = "Your Speech Genie";
    const p = document.createElement("p");
    p.textContent =
      "Welcome — and well played. You just took the first step into Mandarin. " +
      "(The genie intro video plays here.)";
    const mark = document.createElement("div");
    mark.className = "mark";
    mark.textContent = "THE THIRD EAR";
    const go = document.createElement("button");
    go.type = "button";
    go.className = "go";
    go.textContent = "Continue →";
    go.addEventListener("click", () => this._finish());

    ph.append(h, p, mark, go);
    root.replaceChildren(this._styleEl(), ph);
  }

  /** Mark seen for the session and hand off to the menu. Idempotent. */
  _finish() {
    if (this._done) return;
    this._done = true;
    try { sessionStorage.setItem(INTRO_SEEN_KEY, "1"); } catch {}
    try { this._video?.pause(); } catch {}
    navigate(NEXT_ROUTE);
  }

  render() { /* imperative render in onConnected */ }
}
